import { sortBy } from 'lodash'
import _get from 'lodash/get'
import Vue from 'vue'
import Bugsnag from '@bugsnag/js'

const CONTENT_SLOT_RENDER_TIMEOUT_MS = 5000
const reportedSlotFailures = new Set()

const INVISIBLE_UNICODE = /[\u200B-\u200D\uFEFF\u2060\u00AD\u180E\u00A0]/g

export default {
	sanitizeAuthField(value, { trim = false } = {}) {
		let sanitized = (value || '').replace(INVISIBLE_UNICODE, '')
		if (trim) sanitized = sanitized.trim()
		return sanitized
	},

	removeNonNumericCharsOMD(phone) {
		return phone.replace(/\D/g, '')
	},

	// Method to format the phone number as xxx-xxx-xxxx
	newFormatPhoneNumberOMD(phone) {
		const cleaned = ('' + phone).replace(/\D/g, '') // Remove non-numeric characters
		const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

		if (match) {
			return `${match[2]}-${match[3]}-${match[4]}`
		}

		return phone // If it doesn't match the format, return the original
	},

	validateCallBackNumberDashed(number) {
		return !(
			!number ||
			number.length < 12 ||
			number[0] === '0' ||
			number[0] === '1'
		)
	},

	formatPhoneNumberAsStandard(number) {
		// Remove non-numeric characters
		let formatted = number.replace(/\D/g, '')

		// Add the phone number format (xxx-xxx-xxxx)
		if (formatted.length <= 3) {
			number = formatted // First part
		} else if (formatted.length <= 6) {
			number = `${formatted.slice(0, 3)}-${formatted.slice(3)}` // Second part
		} else {
			number = `${formatted.slice(0, 3)}-${formatted.slice(
				3,
				6
			)}-${formatted.slice(6, 10)}` // Final format
		}
		return number
	},

	getDraftValue(key, sid) {
		if (!key || !sid) return ''
		const drafts = JSON.parse(localStorage.getItem('saved_drafts') || '{}')
		return drafts[`${key}_${sid}`] || ''
	},

	setDraftValue(key, value, sid) {
		if (!key || !sid) return
		const drafts = JSON.parse(localStorage.getItem('saved_drafts') || '{}')
		drafts[`${key}_${sid}`] = value
		localStorage.setItem('saved_drafts', JSON.stringify(drafts))
	},

	resetDraftValues() {
		localStorage.removeItem('saved_drafts')
	},

	containNewAlphabets(msg, tmplt, minCount, maxCount) {
		const message = (msg || '').trim().replace(/\s+/g, ' ')
		const template = (tmplt || '').trim() + '\n'

		const countTotalWhitespaces = str => {
			const matches = str.match(/(\r\n)|\r|\n| /g)
			return matches ? matches.length : 0
		}

		const getWords = text =>
			text
				.replace(/[\r\n\t]+/g, ' ')
				.trim()
				.split(/\s+/)

		const messageWords = getWords((message || '').toLowerCase())
		const templateWords = getWords((template || '').toLowerCase())
		let remainingMsgWords = []
		let templateWordsMissingCount = 0
		const templateWordsCopy = [...templateWords]

		for (let i of messageWords) {
			const index = templateWordsCopy.indexOf(i)
			if (index === -1) {
				remainingMsgWords.push(i)
			} else {
				templateWordsCopy.splice(index, 1)
			}
		}

		for (let i of templateWords) {
			const index = messageWords.indexOf(i)
			if (index === -1) {
				templateWordsMissingCount++
			}
		}

		const remainingMsgChars = remainingMsgWords.join('')
		const totalWhitespacesMsg = countTotalWhitespaces(message)
		const totalWhitespacesTemplate = countTotalWhitespaces(template)

		const resultLength =
			remainingMsgChars.length +
			totalWhitespacesMsg -
			(totalWhitespacesTemplate -
				templateWordsMissingCount -
				(templateWordsMissingCount < templateWords.length ? 1 : 0))

		return resultLength >= minCount && resultLength <= maxCount
	},

	normalizeString(str) {
		return str
			.toLowerCase()
			.replace(/\r\n/g, '\n') // normalize CRLF to LF
			.replace(/\s+/g, ' ') // replace multiple whitespace (spaces, tabs, newlines) with single space
			.trim()
	},

	getSpecificCallAttributes(call, attribute, defaultValue = null) {
		const callAttributes = _get(call, 'call_attributes', [])
		const found = callAttributes?.find(
			attr => attr.internal_name === attribute
		)
		const value =
			typeof found?.value === 'string' ? found.value.trim() : found?.value
		return value ? value : defaultValue
	},

	isWMACall(call) {
		const events = call?.events || []
		const hasTransferEvent = events.some(
			event =>
				event.type === 'smart_transfer' ||
				event.type === 'external_transfer'
		)
		const isWmaEnabled = !!call?.wma_allow_reply
		const isCallCompleted = !!call?.completed_at
		return isWmaEnabled && !(isCallCompleted && hasTransferEvent)
	},

	capitalizeFirstLetter(string) {
		if (!string || typeof string !== 'string') return ''
		return string.charAt(0).toUpperCase() + string.slice(1)
	},

	sortPartners(partners = []) {
		return sortBy(partners, p =>
			p.name
				?.toLowerCase()
				.replace(/[()]/g, '') // remove brackets
				.replace(/[\s-]/g, '') // remove spaces & hyphens
				.trim()
		)
	},

	async copyToClipboard(data) {
		try {
			await navigator.clipboard.writeText(data)
			return true
		} catch (err) {
			return false
		}
	},

	contentSlotOverviewBanner: 'overview-banner',

	contentSlotFullscreen: 'fullscreen',

	contentSlotVisibility: Vue.observable({
		fullscreenRemoved: false,
	}),

	openContentSlotLinksInNewTab(html, pageUrl) {
		const baseTag = `<base href="${escapeHtmlAttribute(
			pageUrl
		)}" target="_blank">`
		let documentHtml = String(html || '').replace(
			/<a\b([^>]*?)>/gi,
			(_anchor, attributes) => {
				const href = readHtmlAttribute(attributes, 'href')
				const hrefWithParams = appendContentSlotQueryParams(
					href,
					pageUrl
				)
				let nextAttributes = stripHtmlAttributes(attributes, [
					'target',
					'rel',
				])

				if (hrefWithParams && hrefWithParams !== href) {
					nextAttributes = setHtmlAttribute(
						nextAttributes,
						'href',
						hrefWithParams
					)
				}

				return `<a${nextAttributes} target="_blank" rel="noopener noreferrer">`
			}
		)

		if (/<head[^>]*>/i.test(documentHtml)) {
			return documentHtml.replace(
				/<head[^>]*>/i,
				match => `${match}${baseTag}`
			)
		}

		if (/<html[^>]*>/i.test(documentHtml)) {
			return documentHtml.replace(
				/<html[^>]*>/i,
				match => `${match}<head>${baseTag}</head>`
			)
		}

		return `<!DOCTYPE html><html><head>${baseTag}</head><body>${documentHtml}</body></html>`
	},

	stripContentSlotMainPadding(html) {
		return String(html || '').replace(
			/<main\b([^>]*)>/gi,
			(_main, attributes) => {
				const classValue = readHtmlAttribute(attributes, 'class')

				if (!classValue) {
					return `<main${attributes}>`
				}

				const nextClasses = classValue
					.split(/\s+/)
					.filter(
						className =>
							className && !isPaddingUtilityClass(className)
					)
					.join(' ')

				const nextAttributes = nextClasses
					? setHtmlAttribute(attributes, 'class', nextClasses)
					: stripHtmlAttributes(attributes, ['class'])

				return `<main${nextAttributes}>`
			}
		)
	},

	buildContentSlotUrl(sourceUrl, identity = {}) {
		const source = typeof sourceUrl === 'string' ? sourceUrl.trim() : ''

		if (
			!source ||
			!hasIdentityId(identity.partnerId) ||
			!hasIdentityId(identity.userId)
		) {
			return ''
		}

		let url

		try {
			url = new URL(source)
		} catch (e) {
			return ''
		}

		url.searchParams.set('partnerId', String(identity.partnerId))
		url.searchParams.set('userId', String(identity.userId))
		url.searchParams.set(
			'name',
			buildStaffName(identity.firstName, identity.lastName)
		)
		url.searchParams.set(
			'email',
			identity.email ? String(identity.email) : ''
		)
		url.searchParams.set('isPageLoad', 'true')

		return url.toString()
	},

	resetFullscreenContentSlot() {
		this.contentSlotVisibility.fullscreenRemoved = false
	},

	removeFullscreenContentSlot() {
		this.contentSlotVisibility.fullscreenRemoved = true
	},

	reportContentSlotFailure(slotName) {
		if (reportedSlotFailures.has(slotName)) {
			return
		}

		reportedSlotFailures.add(slotName)
		Bugsnag.notify(new Error(`Content slot failed to render: ${slotName}`))
	},

	startContentSlotRenderTimer(onTimeout) {
		const timerId = window.setTimeout(
			onTimeout,
			CONTENT_SLOT_RENDER_TIMEOUT_MS
		)

		return function clearContentSlotRenderTimer() {
			window.clearTimeout(timerId)
		}
	},
}

const CONTENT_SLOT_QUERY_PARAMS = [
	'partnerId',
	'userId',
	'name',
	'email',
	'isPageLoad',
]

function appendContentSlotQueryParams(href, pageUrl) {
	if (
		!href ||
		href.charAt(0) === '#' ||
		/^(mailto:|tel:|javascript:)/i.test(href)
	) {
		return href
	}

	let targetUrl
	let sourceUrl

	try {
		sourceUrl = new URL(pageUrl)
		targetUrl = new URL(href, pageUrl)
	} catch (e) {
		return href
	}

	CONTENT_SLOT_QUERY_PARAMS.forEach(key => {
		if (!sourceUrl.searchParams.has(key)) {
			return
		}

		targetUrl.searchParams.set(key, sourceUrl.searchParams.get(key))
	})

	return targetUrl.toString()
}

function readHtmlAttribute(attributes, name) {
	const quoted = attributes.match(
		new RegExp(`\\s+${name}\\s*=\\s*(['"])([\\s\\S]*?)\\1`, 'i')
	)

	if (quoted) {
		return decodeHtmlAttribute(quoted[2])
	}

	const unquoted = attributes.match(
		new RegExp(`\\s+${name}\\s*=\\s*([^\\s>]+)`, 'i')
	)

	return unquoted ? decodeHtmlAttribute(unquoted[1]) : ''
}

function setHtmlAttribute(attributes, name, value) {
	const withoutName = stripHtmlAttributes(attributes, [name])

	return `${withoutName} ${name}="${escapeHtmlAttribute(value)}"`
}

function decodeHtmlAttribute(value) {
	return String(value)
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&amp;/g, '&')
}

function escapeHtmlAttribute(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
}

function stripHtmlAttributes(attributes, names) {
	return names.reduce((result, name) => {
		const quoted = new RegExp(
			`\\s+${name}\\s*=\\s*(['"])[\\s\\S]*?\\1`,
			'gi'
		)
		const unquoted = new RegExp(`\\s+${name}\\s*=\\s*[^\\s>]+`, 'gi')

		return result.replace(quoted, '').replace(unquoted, '')
	}, attributes)
}

function isPaddingUtilityClass(className) {
	const utility = String(className)
		.replace(/^!/, '')
		.split(':')
		.pop()
		.replace(/!$/, '')

	return /^(p|px|py|pt|pr|pb|pl)-/.test(utility)
}

function hasIdentityId(value) {
	return value !== undefined && value !== null && String(value).trim() !== ''
}

function buildStaffName(firstName, lastName) {
	return [firstName, lastName]
		.filter(
			part =>
				part !== undefined &&
				part !== null &&
				String(part).trim() !== ''
		)
		.join(' ')
}
